import type { TFunction } from 'i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClimateControl, EmptyState, Skeleton } from '@/ds'
import type { ClimateFeedback } from '@/ds'
import { LinkRow } from '@/app/components/LinkRow'
import { isLanguage } from '@/i18n'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { featureEnabled } from '@/config/features'
import { dayMonthTime, listJoin, locale, telHref, time } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { accesosService } from '@/services/accesos'
import type { KeyStatus } from '@/services/accesos'
import { checkinService } from '@/services/checkin'
import type { Arrival } from '@/services/checkin'
import { climaService } from '@/services/clima'
import type { Climate, ClimateMode, ClimateResult, ClimateZone } from '@/services/clima'
import { isServiceError } from '@/services/errors'
import type { Stay } from '@/services/reserva'
import { useApiLang, useSave } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { GuideItemBlock, PhotoStepList } from './Instrucciones'
import { ACCESS_ROUTES, placeName } from './shared'

/** Espera tras el último toque de −/+ antes de enviar la temperatura, para no mandar un comando por toque. */
const TARGET_DEBOUNCE_MS = 800
/** "Aplicado" se oculta a los 5 s (PantallaClima.dc.html). */
const APPLIED_MS = 5000
const ARRIVAL_ROUTE = '/checkin/paso/6'

const degrees = (n: number, lang: string, decimals = 0) => `${new Intl.NumberFormat(locale(lang), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n)} °C`

/** "sábado 3 de octubre" */
const longDate = (iso: string, lang: string) => new Intl.DateTimeFormat(locale(lang), { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso)).replace(',', '')

/** Fila de la llave encima del clima (PantallaClima.dc.html): resumen con enlace a Accesos. */
function KeySummary({ stay, keyStatus }: { stay: Stay; keyStatus: KeyStatus }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const names = keyStatus.accesses.filter((a) => a.openable).map((a, i) => (i === 0 ? a.name : a.name.toLowerCase()))
  switch (keyStatus.state) {
    case 'active':
      return <LinkRow icon="key" title={t('v2.accesos.llave.resumen.active')} subtitle={keyStatus.activeUntil ? t('v2.accesos.llave.resumen.hasta', { accesses: listJoin(names, lang), when: dayMonthTime(keyStatus.activeUntil, lang) }) : listJoin(names, lang)} to={ACCESS_ROUTES.home} />
    case 'locked':
      return <LinkRow icon="lock" title={t('v2.accesos.llave.resumen.locked')} {...(keyStatus.activeFrom ? { subtitle: t('v2.accesos.llave.resumen.seActiva', { when: dayMonthTime(keyStatus.activeFrom, lang) }) } : {})} to={ACCESS_ROUTES.home} />
    case 'expired':
      return <LinkRow icon="lock" title={t('v2.accesos.llave.resumen.expired')} subtitle={t('v2.accesos.llave.resumen.terminada')} to={ACCESS_ROUTES.home} />
    default:
      return <LinkRow icon="book" title={placeName(stay)} subtitle={t('v2.accesos.llave.resumen.none')} to={ACCESS_ROUTES.instructions} />
  }
}

function arrivalTexts(arrival: Arrival | null, t: TFunction) {
  const slot = arrival?.etaSlot && !('unknown' in arrival.etaSlot) ? arrival.etaSlot : null
  if (!slot) return { arrivalText: t('v2.clima.sinHora'), arrivalHint: t('v2.clima.sinHoraTexto'), changeArrivalLabel: t('v2.clima.indicarHora'), saveDisabled: true }
  return { arrivalText: slot.to ? t('v2.clima.llegada', { from: slot.from, to: slot.to }) : t('v2.clima.llegadaDesde', { from: slot.from }), saveDisabled: false }
}

/** Antes de la estancia: bloqueado y, si la propiedad lo permite, "Prepara la temperatura para tu llegada" (mock, D29/D42). */
function LockedClimate({ stay, climate }: { stay: Stay; climate: Climate }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { go } = useAppNavigate()
  const showArrivalPrep = featureEnabled('climaPrepararLlegada')
  const [prep] = useAsync(`prep:${stay.guestCode}`, (signal) => (showArrivalPrep ? climaService.getArrivalPrep(stay, signal) : Promise.resolve(null)))
  const [arrival] = useAsync(`arr:${stay.guestCode}`, (signal) => checkinService.getArrival(stay, signal))
  const [target, setTarget] = useState<number | null>(null)
  const [saved, setSaved] = useState<boolean | null>(null)
  const { saving, error, run } = useSave()
  if (prep.kind === 'loading' || arrival.kind === 'loading') return <Skeleton height={420} radius={24} />
  const p = showArrivalPrep && prep.kind === 'ready' ? prep.value : null
  const from = climate.activeFrom
  const value = target ?? p?.target ?? 22
  const save = () =>
    void run(async () => {
      const r = await climaService.setArrivalPrep(stay, value)
      setTarget(r.target)
      setSaved(true)
      return r
    })
  return (
    <>
      <ClimateControl
        variant="connected"
        locked
        {...(from ? { lockedChip: t('v2.clima.seActiva', { when: dayMonthTime(from, lang) }), lockedText: t('v2.clima.bloqueadoTexto', { date: longDate(from, lang), time: time(from, lang) }) } : {})}
        {...(p
          ? {
              arrivalPrep: {
                target: value,
                min: p.min,
                max: p.max,
                saved: saved ?? p.saved,
                onTargetChange: setTarget,
                onSave: () => !saving && save(),
                onChangeArrival: () => go(ARRIVAL_ROUTE),
                ...arrivalTexts(arrival.kind === 'ready' ? arrival.value : null, t),
              },
            }
          : {})}
      />
      {error && (
        <p className="accesos__error" role="alert">
          {error}
        </p>
      )}
    </>
  )
}

type Action = { kind: 'target'; value: number } | { kind: 'mode'; mode: ClimateMode } | { kind: 'preset'; id: string }

/** Durante la estancia: control conectado sobre E16/E17 (D13), con aplicando → aplicado | error. */
function ConnectedClimate({ stay, climate }: { stay: Stay; climate: Climate }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [zones, setZones] = useState<ClimateZone[]>(climate.zones)
  const [zoneId, setZoneId] = useState(climate.zones[0]?.id ?? '')
  const [target, setTargetValue] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<ClimateFeedback>(null)
  const [offline, setOffline] = useState(false)
  const last = useRef<Action | null>(null)
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const hide = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(
    () => () => {
      clearTimeout(debounce.current)
      clearTimeout(hide.current)
    },
    [],
  )
  const zone = zones.find((z) => z.id === zoneId) ?? zones[0]
  const range = climate.range

  const apply = useCallback(
    async (action: Action, onZone: ClimateZone) => {
      last.current = action
      clearTimeout(hide.current)
      setOffline(false)
      setFeedback('applying')
      // E17 recibe el idioma sin mapear (`en`, no `gb`), como useAirConditioning.tsx.
      const apiLang = isLanguage(lang) ? lang : 'es'
      let result: ClimateResult
      try {
        result =
          action.kind === 'target'
            ? await climaService.setTarget(stay, onZone, action.value, apiLang)
            : action.kind === 'mode'
              ? await climaService.setMode(stay, onZone, action.mode, apiLang)
              : await climaService.applyPreset(stay, onZone, action.id, apiLang)
      } catch (e) {
        setOffline(isServiceError(e) && e.code === 'offline')
        setFeedback('error')
        return
      }
      if (result.status === 'error') return setFeedback('error')
      const updated = result.zone
      setZones((list) => list.map((z) => (z.id === updated.id ? updated : z)))
      setTargetValue(null)
      setFeedback('applied')
      hide.current = setTimeout(() => setFeedback(null), APPLIED_MS)
    },
    [stay, lang],
  )

  if (!zone || !range || zone.target === null) {
    return <EmptyState tone="error" title={t('v2.ds.climate.errorTitulo')} headingLevel={2}>{t('v2.ds.climate.errorTexto')}</EmptyState>
  }
  const shownTarget = target ?? zone.target
  const onDelta = (delta: number) => {
    const next = Math.min(range.max, Math.max(range.min, Math.round((shownTarget + delta) * 2) / 2))
    setTargetValue(next)
    clearTimeout(debounce.current)
    debounce.current = setTimeout(() => void apply({ kind: 'target', value: next }, zone), TARGET_DEBOUNCE_MS)
  }
  const alert = featureEnabled('climaAvisos') ? climate.alerts.find((a) => a.zoneId === null || a.zoneId === zone.id) : undefined
  const presets = featureEnabled('climaAtajos') ? climate.presets.map((p) => ({ id: p.id, label: p.name, sublabel: t('v2.clima.atajo', { temp: degrees(p.target, lang), mode: t(`v2.estancia.clima.modo.${p.mode}`) }) })) : []
  const modes = climate.modes.filter((m) => m !== 'auto' || featureEnabled('climaAutomatico'))
  const selectedMode = modes.includes(zone.mode) ? zone.mode : (modes[0] ?? zone.mode)
  return (
    <>
      <ClimateControl
        variant="connected"
        zones={zones.map((z) => ({ id: z.id, name: z.name }))}
        zoneId={zone.id}
        onZoneChange={(id) => {
          // Cambiar de zona no aplica nada (Bloque 8, notas).
          clearTimeout(debounce.current)
          setTargetValue(null)
          setFeedback(null)
          setZoneId(id)
        }}
        {...(zone.current !== null ? { current: { temp: zone.current, mode: zone.mode, zoneName: zone.name.toLowerCase() } } : {})}
        target={shownTarget}
        range={range}
        onTargetDelta={onDelta}
        feedback={feedback}
        onRetry={() => last.current && void apply(last.current, zone)}
        {...(stay.contact.supportPhone ? { phone: stay.contact.supportPhone } : {})}
        modes={modes.map((m) => ({ id: m, label: t(`v2.ds.climate.modo.${m}`) }))}
        mode={selectedMode}
        onModeChange={(mode) => void apply({ kind: 'mode', mode }, zone)}
        modesHelp={t('v2.clima.modosAyuda', { count: modes.length })}
        presets={presets}
        onPreset={(id) => void apply({ kind: 'preset', id }, zone)}
        {...(alert && feedback === null
          ? { alert: alert.code === 'window_open' ? { title: t('v2.clima.avisoVentana', { zone: zone.name }), text: t('v2.clima.avisoVentanaTexto') } : { title: t('v2.clima.avisoEquipo'), text: alert.text ?? '' } }
          : {})}
      />
      {offline && (
        <p className="accesos__error" role="alert">
          {t('v2.clima.sinConexion')}
        </p>
      )}
    </>
  )
}

/** Propiedad sin termostato conectado: instrucciones con fotos (mock) o el ítem de la guía (real, D13). */
function StaticClimate({ stay, climate }: { stay: Stay; climate: Climate }) {
  const { t } = useTranslation()
  const phone = stay.contact.supportPhone
  const contactHref = stay.contact.whatsapp ? whatsappHref(stay.contact.whatsapp, stay.reference) : phone ? telHref(phone) : null
  const contact = contactHref ? (
    <a href={contactHref} {...(stay.contact.whatsapp ? { target: '_blank', rel: 'noreferrer' } : {})}>
      {t(stay.contact.whatsapp ? 'v2.ds.contact.whatsapp' : 'v2.ds.contact.llamar')}
    </a>
  ) : null
  const showPhotoSteps = featureEnabled('accesosPasosFoto')
  const steps = showPhotoSteps && climate.steps.length > 0 ? <PhotoStepList steps={climate.steps} /> : climate.instructions ? <GuideItemBlock item={climate.instructions} /> : null
  return (
    <>
      <ClimateControl
        variant="static"
        staticInstructions={{
          intro: showPhotoSteps && climate.steps.length > 0 ? t('v2.clima.estaticoIntro') : t('v2.clima.estaticoGuia'),
          steps,
          ...(climate.houseRule ? { houseRule: climate.houseRule, contact } : {}),
        }}
      />
      {!climate.houseRule && contact && <p className="accesos__row-text">{contact}</p>}
    </>
  )
}

/**
 * 8.4b · Clima (PantallaClima.dc.html, Bloque 8 §8a–8g; design/prompt-clima.md). Dentro de Accesos, bajo la
 * llave. Mismas condiciones que la llave (`showDomotic` manda, D6); tras el check-out no hay control.
 * Real: zonas, lectura (E16), temperatura y modo (E17). Mock: medio grado, automático, atajos, avisos,
 * pasos con foto y preparar la llegada (docs/backend/clima.md).
 */
export function Clima() {
  const { t } = useTranslation()
  const stay = useLoadedStay()
  const apiLang = useApiLang()
  const cacheKey = `${stay.guestCode}|${stay.phase}`
  const [key] = useAsync(`key:${cacheKey}`, (signal) => accesosService.getKey(stay, signal))
  const [climate, reload] = useAsync(`clima-full:${cacheKey}:${apiLang}`, (signal) => climaService.getClimate(stay, apiLang, signal))
  const c = climate.kind === 'ready' ? climate.value : null
  return (
    <main className="page">
      <div className="accesos__head">
        <h1>{t('v2.accesos.titulo')}</h1>
      </div>
      {key.kind === 'ready' && <KeySummary stay={stay} keyStatus={key.value} />}
      {climate.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={480} radius={24} />
        </div>
      )}
      {climate.kind === 'error' && <SectionError code={climate.code} onRetry={reload} />}
      {c && !c.available && (
        <EmptyState icon="thermometer" title={t('v2.clima.noDisponible')} headingLevel={2}>
          {t('v2.clima.noDisponibleTexto')}
        </EmptyState>
      )}
      {c?.available && c.variant === 'static' && <StaticClimate stay={stay} climate={c} />}
      {c?.available && c.variant === 'connected' && c.state === 'unavailable' && (
        <EmptyState icon="thermometer" title={t('v2.clima.terminado')} headingLevel={2}>
          {t('v2.clima.terminadoTexto')}
        </EmptyState>
      )}
      {c?.available && c.variant === 'connected' && c.state === 'locked' && <LockedClimate stay={stay} climate={c} />}
      {c?.available && c.variant === 'connected' && c.state === 'active' && <ConnectedClimate key={cacheKey} stay={stay} climate={c} />}
    </main>
  )
}
